import { ComposedGesture, GestureType } from "react-native-gesture-handler";
import { SharedValue } from "react-native-reanimated";
import { PhotoDetailLazyPhotoFragmentFragment } from "src/api/graphql/__generated__/graphql";
import EventTopic from "src/helpers/EventTopic";
import { Timeout } from "src/types/time";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { PhotoItem } from "../types";

export interface PhotoDetailStore {
	/**
	 * Debounced. If the user is paging quickly, then this value will continuously update every 300ms
	 */
	debouncedCurPhoto: PhotoItem | null;
	/**
	 * Debounced. This only updates after the photo is settled. If the user is paging quickly, this value will only update once the user has stopped paging.
	 */
	debouncedSettledPhoto: PhotoItem | null;
	/**
	 * Async-loaded data of the settled photo
	 */
	asyncSettledPhoto: PhotoDetailLazyPhotoFragmentFragment | null;
	/**
	 * WARNING: This is not debounced
	 */
	curPhoto: PhotoItem | null;
	setCurPhoto: (photo: PhotoItem) => void;
	/**
	 * Used to update CurPhoto without causing race conditions with `setCurPhoto` used in ImageGallery
	 */
	setCurPhotoIfMatching: (photo: PhotoItem) => void;
	setDidLoad: (
		photo: { photoId: string },
		loadInfo: {
			success: boolean;
			isInitial: boolean;
			didFail?: boolean;
			didLoadDetailed?: boolean;
		},
	) => void;
	_debouncedSettledPhotoTimeout: Timeout | null;
	_debouncedCurPhotoTimeout: Timeout | null;
	/**
	 * Lookup for if the main photo item did load or not
	 */
	_didLoadLookup: Record<
		string,
		{
			didLoad: boolean;
			success: boolean;
			didFail?: boolean;
			didLoadDetailed?: boolean;
		}
	>;
	isWaitingForCurPhotoLoad: boolean;
	isWaitingForCurPhotoDetailedLoad: boolean;
	isWaitingForInitialPhotoLoad: boolean;
	mode: "Zoom" | "FullScreen" | "Regular" | "Carousel";
	zoomScale: SharedValue<number> | null;
	zoomXOffset: SharedValue<number> | null;
	zoomYOffset: SharedValue<number> | null;
	hideNavigationSubscribers: string[];
	createMainContentGesture: null | (() => ComposedGesture | GestureType);
	clear: () => void;
	setMode: (mode: "Zoom" | "FullScreen" | "Regular" | "Carousel") => void;
	setZoomHooks: (sv: {
		zoomScale: SharedValue<number>;
		zoomXOffset: SharedValue<number>;
		zoomYOffset: SharedValue<number>;
	}) => void;
	clearZoomHooks: () => void;
	listenerTopic: EventTopic<{
		refreshAsyncSettledPhoto: {
			props: {};
		};
	}>;
}
const usePhotoDetailStore = create<PhotoDetailStore>()(
	subscribeWithSelector((set, get) => ({
		listenerTopic: new EventTopic(),
		curPhoto: null,
		blockingGestureRef: { current: null },
		debouncedCurPhoto: null,
		debouncedSettledPhoto: null,
		asyncSettledPhoto: null,
		isWaitingForCurPhotoLoad: false,
		isWaitingForCurPhotoDetailedLoad: false,
		isWaitingForInitialPhotoLoad: false,
		_debouncedSettledPhotoTimeout: null,
		_debouncedCurPhotoTimeout: null,
		_didLoadLookup: {},
		mode: "Regular",
		zoomScale: null,
		zoomXOffset: null,
		zoomYOffset: null,
		hideNavigationSubscribers: [],
		createMainContentGesture: null,
		setCurPhoto: (photo: PhotoItem) => {
			const photoLoad = get()._didLoadLookup[photo.photoId];
			set({
				curPhoto: photo,
				isWaitingForCurPhotoLoad: !(photoLoad?.didLoad ?? false),
				isWaitingForCurPhotoDetailedLoad: !(photoLoad?.didLoadDetailed ?? false),
			});

			const oldSettledPhotoTimeout = get()._debouncedSettledPhotoTimeout;
			if (oldSettledPhotoTimeout) {
				clearTimeout(oldSettledPhotoTimeout);
			}
			const newSettledPhotoTimeout = setTimeout(() => {
				set({
					debouncedSettledPhoto: photo,
					_debouncedSettledPhotoTimeout: null,
				});
			});
			set({
				_debouncedSettledPhotoTimeout: newSettledPhotoTimeout,
			});

			const oldCurPhotoTimeout = get()._debouncedCurPhotoTimeout;
			if (!oldCurPhotoTimeout) {
				// Primarily used for debouncing window url replace state
				// Ideally keep below 30 requests per 10 seconds
				const timeout = setTimeout(() => {
					const newCurPhotoTimeout = get().curPhoto;
					set({
						debouncedCurPhoto: newCurPhotoTimeout,
						_debouncedCurPhotoTimeout: null,
					});
				}, 300);
				set({
					_debouncedCurPhotoTimeout: timeout,
				});
			}
		},
		setCurPhotoIfMatching: (photo: PhotoItem) => {
			const curPhotoId = get().curPhoto?.photoId;
			if (curPhotoId === photo.photoId) {
				set({
					curPhoto: photo,
				});
			}
		},
		setDidLoad: ({ photoId }, { success, isInitial, didFail, didLoadDetailed }) => {
			const { curPhoto, _didLoadLookup } = get();
			_didLoadLookup[photoId] = {
				didLoad: true,
				success: success,
			};
			if (didLoadDetailed != null) {
				_didLoadLookup[photoId]!.didLoadDetailed = didLoadDetailed;
			}
			if (didFail != null) {
				_didLoadLookup[photoId]!.didFail = didFail;
			}

			const setObj: Partial<PhotoDetailStore> = {
				_didLoadLookup: _didLoadLookup,
			};
			if (curPhoto && curPhoto.photoId === photoId) {
				setObj.isWaitingForCurPhotoLoad = false;
				if (isInitial) {
					setObj.isWaitingForInitialPhotoLoad = false;
				}
				if (didLoadDetailed) {
					setObj.isWaitingForCurPhotoDetailedLoad = false;
				}
			}
			set(setObj);
		},
		setMode: (mode) => {
			set({
				mode,
			});
		},
		clear: () => {
			const timeout = get()._debouncedCurPhotoTimeout;
			if (timeout) {
				clearTimeout(timeout);
			}
			set({
				isWaitingForCurPhotoLoad: false,
				isWaitingForCurPhotoDetailedLoad: false,
				isWaitingForInitialPhotoLoad: false,
				debouncedCurPhoto: null,
				debouncedSettledPhoto: null,
				_debouncedCurPhotoTimeout: null,
				_debouncedSettledPhotoTimeout: null,
				_didLoadLookup: {},
				curPhoto: null,
				mode: "Regular",
				zoomScale: null,
				zoomXOffset: null,
				zoomYOffset: null,
				createMainContentGesture: null,
				hideNavigationSubscribers: [],
			});
		},
		setZoomHooks: ({ zoomScale, zoomXOffset, zoomYOffset }) => {
			set({
				zoomScale,
				zoomXOffset,
				zoomYOffset,
			});
		},
		clearZoomHooks: () => {
			set({
				zoomScale: null,
				zoomXOffset: null,
				zoomYOffset: null,
			});
		},
	})),
);

export default usePhotoDetailStore;
